/* eslint-disable react/prop-types */
import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useRoute } from "wouter";
import { z } from "zod";

import { Language, useLanguage } from "../../i18n/useLanguage";
import { NationalVendorLandingRoute } from "../../navigation/NationalRoutes";
import { getDealerBasePath } from "../../navigation/getDealerBasePath";
import { getNationalBasePath } from "../../navigation/getNationalBasePath";
import {
  DealerInfo,
  useGetDealerGroupDealerList,
} from "../../queries/useGetDealerGroupDealerList";
import { useIsNationalSite } from "../../stores/hooks/useIsNationalSite";
import { useSearchForm } from "../../stores/hooks/useSearchForm";
import { PrimaryButton } from "../../ui/Button/Button";
import { useForm } from "../../util/useForm";
import { useIsDealerGroup } from "../DealerGroup/useIsDealerGroup";
import { dealershipModeLocality } from "../Dealerships/DealershipForm";
import { DealerInput } from "./inputs/DealerInput";
import { PostalCodeInput } from "./inputs/PostalCodeInput";
import { TireSizeInput } from "./inputs/TireSizeInput";

const schema = z.object({
  postalCode: z.string().optional(),
  tireSize: z.string().nonempty({ message: "form.error.tireSize" }),
});

const getBasePath = ({
  isDealerGroup,
  isNational,
  language,
  selectedDealer,
}: {
  isDealerGroup: boolean;
  isNational: boolean;
  language: Language;
  selectedDealer: DealerInfo | null;
}): null | string => {
  if (isDealerGroup) {
    if (!selectedDealer) {
      return null;
    }
    return `/${selectedDealer.dealerUrlCode}/${language}/dealer/${selectedDealer.dealerId}`;
  }
  if (isNational) {
    return getNationalBasePath();
  }
  return getDealerBasePath();
};

export const TireSize: FunctionComponent = () => {
  const { t } = useTranslation();
  const [shouldRenderPostCode] = useRoute(NationalVendorLandingRoute);
  const [{ dealerId }] = useSearchForm();
  const isNational = useIsNationalSite();
  const isDealerGroup = useIsDealerGroup();
  const { selectedDealer } = useGetDealerGroupDealerList();
  const [language] = useLanguage();

  const [, setLocation] = useLocation();
  const [handleForm] = useForm({
    onSubmit({ postalCode, tireSize }) {
      const basePath = getBasePath({
        isDealerGroup,
        isNational,
        language,
        selectedDealer,
      });
      if (!basePath) {
        return;
      }
      const encodedTireSize = encodeURIComponent(tireSize);
      const nextPath = [
        `${basePath}/search/tiresize-search/${encodedTireSize}`,
        shouldRenderPostCode &&
          postalCode &&
          `q=${postalCode}&mode=${dealershipModeLocality}&distance=300`,
      ]
        .filter(Boolean)
        .join("?");

      if (isDealerGroup) {
        window.open(nextPath, "_blank");
      } else {
        setLocation(nextPath);
      }
    },
    schema,
  });
  return (
    <form
      className={clsx("grid sm:grid-cols-2 xl:flex gap-2")}
      noValidate
      onSubmit={handleForm}
    >
      {isDealerGroup && <DealerInput />}
      <TireSizeInput />
      {shouldRenderPostCode && <PostalCodeInput />}
      <PrimaryButton
        className={clsx(
          "shrink-0 shadow-lg h-12 md:h-14",
          shouldRenderPostCode && "sm:col-span-2"
        )}
        aria-label="viewResults"
        disabled={isDealerGroup ? !dealerId : false}
        type="submit"
      >
        {t("searchForm.viewResults")}
      </PrimaryButton>
    </form>
  );
};
